import { defineStore } from 'pinia'
import { signOut, fetchAuthSession, getCurrentUser, fetchUserAttributes, signIn } from "aws-amplify/auth"; 
import { notify } from '@components/NotificationPlugin';

interface State {
  isLogged: boolean
  firstName: string
  lastName: string
  userId: string | null
  group: string[] | null
  _token: any | null
  email: string
  phone: string
  user: any | null
  authStatus: 'loading' | 'authenticated' | 'unauthenticated' | 'error'
  authError: string | null
  userGroups: string[]
  userSharedGroups: string[]
  isIdentityPoolAuth: boolean
}

// Helper to check if authentication is via Identity Pool only
const isIdentityPoolAuth = async () => {
  try {
    const session = await fetchAuthSession();
    
    // First check if we have Identity Pool credentials
    const hasIdentityCredentials = !!(session && session.credentials && session.identityId);
    
    if (!hasIdentityCredentials) {
      return false; // No identity credentials at all
    }
    
    // Now check if we also have User Pool tokens
    const hasUserPoolTokens = !!(session.tokens && session.tokens.idToken);
    
    // If we have Identity Pool credentials but no User Pool tokens,
    // we're using Identity Pool authentication only
    if (hasIdentityCredentials && !hasUserPoolTokens) {
      // Double-check by attempting to get the current user
      try {
        await getCurrentUser();
        // If getCurrentUser succeeds, we actually do have a User Pool user
        return false;
      } catch (userError) {
        // If getCurrentUser fails, we confirm we're using Identity Pool auth only
        return true;
      }
    }
    
    // We have both Identity Pool credentials and User Pool tokens
    return false;
  } catch (error) {
    console.error('Error checking for identity pool auth:', error);
    return false;
  }
};

export const getSession = async () => {
  try {
    const { tokens, credentials, identityId, userSub } = await fetchAuthSession();
    return { tokens, credentials, identityId, userSub };
  } catch (error) {
    console.error('Error getting session:', error);
    throw error; // Re-throw after logging
  }
}

export const getAuthenticatedUser = async () => {
  try {
    const {
      username,
      signInDetails
    } = await getCurrentUser();

    const {
      tokens: session
    } = await fetchAuthSession();

    const attributes = await fetchUserAttributes();
    
    return { 
      username, 
      signInDetails, 
      session, 
      attributes,
      authenticationFlowType: signInDetails?.authFlowType
    };
  } catch (error) {
    console.error('Error getting authenticated user:', error);
    throw error;
  }
}

let isLoggingOut = false;

export const useAuthUserStore = defineStore('auth', {
  state: (): State => ({ 
    isLogged: false,
    firstName: '',
    lastName: '',
    userId: null,
    group: null,
    _token: null,
    email: '',
    phone: '',
    user: null,
    authStatus: 'loading',
    authError: null,
    userGroups: [],
    userSharedGroups: [],
    isIdentityPoolAuth: false
  }),
  getters: {
    // Get user's full name
    fullName: (state) => {
      if (state.firstName && state.lastName) {
        return `${state.firstName} ${state.lastName}`;
      } else if (state.firstName) {
        return state.firstName;
      } else if (state.email) {
        return state.email;
      }
      return 'User';
    },
    // Check if user is authenticated
    isAuthenticated: (state) => { return state.isLogged },
    // Check if user is in a specific group
    isInGroup: (state) => (groupName: string) => {
      return state.userGroups.includes(groupName);
    },
    // Check if user is authorized for a specific role.
    // SuperAdmins inherit every lower role (mirrors the API, where group
    // checks substring-match, so a super admin also satisfies Admins gates).
    isAuthorized: (state) => (requiredGroup: string | null) => {

      if (!requiredGroup) return true;
      if (!state.isLogged) return false;
      if (requiredGroup === '' || (requiredGroup === 'Users' && state._token)) return true;
      if (!state.userGroups) return false;
      if (state.userGroups.includes('SuperAdmins')) return true;
      return state.userGroups.includes(requiredGroup);
    },
    // Get JWT token
    token: (state) => state._token,
    // Additional getters from Vuex implementation
    selectedUserId: (state) => state.userId,
    groups: (state) => state.group,
    isPremium: (state) => state.user?.attributes?.['custom:isPremium'] === 'true',
    subscriptionEndDate: (state) => state.user?.attributes?.['custom:subscriptionEndDate'],
  },
  actions: {
    // Login with username and password
    async login(username: string, password: string) {
      try {
        this.authStatus = 'loading';
        this.authError = null;
        
        const { isSignedIn, nextStep } = await signIn({
          username,
          password,
        });
        
        if (isSignedIn) {
          await this.getUser();
          try {
            const { trackEvent } = await import('@utils/analytics');
            trackEvent('signed_in', {});
          } catch { /* analytics must never break login */ }
          return { success: true };
        } else {
          // Handle MFA or other authentication challenge steps
          return { success: false, nextStep };
        }
      } catch (error: any) {
        this.authStatus = 'error';
        this.authError = error.message || 'Authentication failed';
        
        return { success: false, error: this.authError };
      }
    },
    
    // Logout the user
    async logout() {
      if (isLoggingOut || !this.isLogged) return;
      
      try {
        isLoggingOut = true;
        await signOut();
        this.clearUser();
     
        return true;
      } catch (error: any) {
        console.error('Error signing out:', error);
        
        return false;
      } finally {
        isLoggingOut = false;
      }
    },
    
    // Get current token
    async getCurrentToken(force = false): Promise<string | null> {
      if (!this.isLogged) {
        return null;
      }

      try {
        const data = await fetchAuthSession({ forceRefresh: force });
        const token = data?.tokens?.idToken?.toString();
        if (token) {
          return token;
        }

        // Amplify resolves with an empty session (no throw) when
        // unauthenticated — force one real refresh before deciding.
        if (!force) {
          return this.getCurrentToken(true);
        }

        // Confirmed dead session: mark logged-out so the route guard can
        // redirect to login instead of issuing unauthenticated calls that
        // surface as opaque network errors.
        this.isLogged = false;
        return null;
      } catch (error) {
        // Transient failure (network blip, Amplify-configure startup race):
        // do NOT clear isLogged — the next request retries. Only a
        // confirmed-empty session flips it (above).
        console.error('Error getting current token:', error);
        return null;
      }
    },
    
    // Get user data from Cognito User Pool
    async getUser() {
      try {
        this.authStatus = 'loading';
        
        // First, check if this is Identity Pool authentication
        const isIdentityAuth = await isIdentityPoolAuth();
        
        if (isIdentityAuth) {
          // Handle Identity Pool authentication
          return this.getIdentityPoolUser();
        } else {
          // Handle User Pool authentication
          try {
            const data = await getAuthenticatedUser();
            
            if (data && data.session) {
              // Update the user state
              this.updateUser(data);
              
              const user = {
                username: data.username,
                attributes: {
                  email: data.attributes?.email || '',
                  'custom:isPremium': data.attributes['custom:isPremium'],
                  'custom:subscriptionEndDate': data.attributes['custom:subscriptionEndDate'],
                  'custom:userGroups': data.attributes['custom:userGroups']
                },
                session: data.session,
                isIdentityPoolAuth: false
              };
              
              this.user = user;
              this.isIdentityPoolAuth = false;
              return data;
            }
          } catch (error: any) {
            // Handle special case for Identity Pool only authentication
            if (error.message && error.message.includes('User needs to be authenticated')) {
              return this.getIdentityPoolUser();
            }
            throw error;
          }
        }
        
        return null;
      } catch (error) {
        console.error('Error in getUser:', error);
        this.clearUser();
        this.authStatus = 'unauthenticated';
        return null;
      }
    },
    
    // Get user data from Identity Pool
    async getIdentityPoolUser() {
      try {
        const sessionData = await fetchAuthSession();
        
        if (sessionData && sessionData.credentials && sessionData.identityId) {
          // Get any stored social user info
          const socialUserInfo = localStorage.getItem('socialUserInfo');
          let userInfo:any = {};
          
          if (socialUserInfo) {
            try {
              userInfo = JSON.parse(socialUserInfo);
            } catch (e) {
              console.error('Error parsing social user info:', e);
            }
          }
          
          // Create a user object with identity pool info
          const user = {
            username: userInfo.email || userInfo.name || sessionData.identityId,
            attributes: {
              email: userInfo.email || '',
              name: userInfo.name || 'User',
              picture: userInfo.picture || '',
              identityId: sessionData.identityId,
              lastName: userInfo.lastName || '', 
              firstName: userInfo.firstName || ''
            },
            session: {
              credentials: sessionData.credentials,
              identityId: sessionData.identityId
            },
            isIdentityPoolAuth: true
          };
          
          this.user = user;
          this.firstName = user.attributes.name;
          this.lastName = user.attributes.lastName || '';
          this.userId = sessionData.identityId;
          this.userGroups = ['Users']; // Default group for identity pool users
          this.userSharedGroups = []; 
          this.group = ['Users'];
          this.isLogged = true;
          this.authStatus = 'authenticated';
          this.isIdentityPoolAuth = true;
          
          return user;
        }
        
        return null;
      } catch (error) {
        console.error('Error getting identity pool user:', error);
        this.clearUser();
        return null;
      }
    },
    
    // Update user state
    updateUser(payload: any) {
      if (payload === null) {
        this.clearUser();
      } else {
        if (payload && payload.session && payload.session.idToken) {
          // payload carries session tokens + user attributes — never log it.
          this.firstName = payload.username;
          this.userId = payload.attributes.sub;
          this._token = payload.session.idToken;
         
          if (payload.attributes.email) {
            this.email = payload.attributes.email;
          }
          
          if (payload.session.idToken.payload['custom:userGroups']) {
            this.userSharedGroups =  payload.session.idToken.payload['custom:userGroups'].split(',');
          }
          if (payload.session.idToken.payload['cognito:groups']) {
            const group = payload.session.idToken.payload['cognito:groups'];
            if (!group.includes('Users')) {
              group.push('Users');
            }
            this.group = group;
            this.userGroups = group;
          } else {
            this.group = ['Users'];
            this.userGroups = ['Users'];
          }
          
          this.isLogged = true;
          this.authStatus = 'authenticated';
        } else {
          this._token = null;
          this.group = null;
          this.userGroups = [];
          this.isLogged = false;
          this.authStatus = 'unauthenticated';
        }
      }
    },
    
    // Clear user data
    clearUser() {
      this.isLogged = false;
      this.firstName = '';
      this.lastName = '';
      this.userId = null;
      this.group = null;
      this._token = null;
      this.email = '';
      this.phone = '';
      this.user = null;
      this.authStatus = 'unauthenticated';
      this.authError = null;
      this.userGroups = [];
      this.userSharedGroups = [];

      this.isIdentityPoolAuth = false;
    },
    
    // Get user data from local storage
    getUserData() {
      const cognitoAppClientId = localStorage.getItem('userPoolClientId') || null;
      if (cognitoAppClientId && this.userId) {
        const userDataKey = 'CognitoIdentityServiceProvider.' + cognitoAppClientId + '.' + this.userId + '.' + 'userData';
        const userData = localStorage.getItem(userDataKey);
        if (userData) {
          try {
            return JSON.parse(userData);
          } catch {
            return {};
          }
        }
      }
      return {};
    },
    
    // Initialize auth state
    async initialize() {
      try {
        const user = await this.getUser();
        return user !== null;
      } catch (error) {
        this.clearUser();
        return false;
      }
    },
    
    // Social login handler
    async socialLogin(provider: string) {
      // This would be implemented based on how your auth.service handles federated logins
      notify({
        message: `Social login with ${provider} initiated`,
        type: 'info',
        icon: 'tim-icons icon-bulb-63'
      });
      
      // After successful social login, you would call getIdentityPoolUser()
      // to handle the identity pool authentication
    },
    
  },
  persist: true
});